import { useEffect, useRef } from 'react';

const DEFAULT_SCROLL_EDGE_THRESHOLD_PX = 60;
const DEFAULT_MAX_SCROLL_SPEED_PX = 18;

const resolveContainer = (target) => {
  if (typeof target === 'string') return document.querySelector(target);
  if (target && 'current' in target) return target.current;
  return target || null;
};

const useDragAutoScroll = (target, isDragging, clientOffset, options = {}) => {
  const {
    edgeThreshold = DEFAULT_SCROLL_EDGE_THRESHOLD_PX,
    maxSpeed = DEFAULT_MAX_SCROLL_SPEED_PX,
  } = options;

  const offsetRef = useRef(clientOffset);
  offsetRef.current = clientOffset;

  useEffect(() => {
    if (!isDragging) return undefined;

    const container = resolveContainer(target);
    if (!container) return undefined;

    let rafId = null;

    const step = () => {
      const offset = offsetRef.current;
      if (offset) {
        const rect = container.getBoundingClientRect();
        const distFromTop = offset.y - rect.top;
        const distFromBottom = rect.bottom - offset.y;

        if (distFromTop < edgeThreshold && distFromTop > 0) {
          const ratio = 1 - distFromTop / edgeThreshold;
          container.scrollTop -= maxSpeed * ratio;
        } else if (distFromBottom < edgeThreshold && distFromBottom > 0) {
          const ratio = 1 - distFromBottom / edgeThreshold;
          container.scrollTop += maxSpeed * ratio;
        }
      }
      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [target, isDragging, edgeThreshold, maxSpeed]);
};

export default useDragAutoScroll;
